<template>
  <div class="home-page">

    <!-- Hero Section - Full Bleed -->
    <HeroSection />

    <!-- Featured Wallpapers Section -->
    <section class="featured-section">
      <div class="section-container">
        <div class="section-header">
          <div class="section-header-left">
            <span class="section-label">Curated Collection</span>
            <h2 class="section-title">Featured Wallpapers</h2>
          </div>
          <router-link to="/categories" class="view-all-link">
            View All
            <svg class="view-all-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </router-link>
        </div>

        <!-- View Mode Toggle -->
        <div class="view-toggle-wrap">
          <div class="view-toggle">
            <button @click="viewMode = 'grid'" :class="['view-toggle-btn', viewMode === 'grid' && 'active']">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              Grid
            </button>
            <button @click="viewMode = 'preview'" :class="['view-toggle-btn', viewMode === 'preview' && 'active']">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Preview
            </button>
          </div>
        </div>

        <!-- Wallpaper Grid -->
        <WallpaperGrid :wallpapers="featuredWallpapers" :loading="loading" :has-more="false" :view-mode="viewMode"
          :category-map="categoryMap" @wallpaper-click="viewWallpaper" />
      </div>
    </section>

    <!-- Ad Banner after featured wallpapers -->
    <SmartAd ad-type="banner" class="ad-spacer" :min-content-height="500" />

    <!-- Categories Section - Horizontal Scroll -->
    <section class="categories-section">
      <div class="section-container">
        <div class="section-header">
          <div class="section-header-left">
            <span class="section-label">Explore</span>
            <h2 class="section-title">Popular Categories</h2>
          </div>
          <router-link to="/categories" class="view-all-link">
            See All
            <svg class="view-all-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </router-link>
        </div>

        <!-- Loading State for Categories -->
        <div v-if="categoriesLoading" class="categories-scroll">
          <div v-for="n in 5" :key="`skeleton-${n}`" class="category-card skeleton">
            <div class="skeleton-icon"></div>
            <div class="skeleton-text-lg"></div>
            <div class="skeleton-text-sm"></div>
          </div>
        </div>

        <!-- Categories Horizontal Scroll -->
        <div v-else class="categories-scroll">
          <router-link
            v-for="category in popularCategories"
            :key="category.name"
            :to="`/categories?category=${category.name}`"
            class="category-card"
          >
            <div class="category-icon">{{ category.icon }}</div>
            <div class="category-name">{{ category.name }}</div>
            <div v-if="category.description" class="category-desc">{{ category.description }}</div>
            <div class="category-count">
              {{ formatNumber(category.count) }} {{ category.count === 1 ? 'wallpaper' : 'wallpapers' }}
            </div>
          </router-link>
        </div>
      </div>
    </section>

    <!-- Stats Section - Floating Counters -->
    <section class="stats-section">
      <div class="section-container">
        <div class="stats-grid">
          <div class="stat-item">
            <div class="stat-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div v-if="statsLoading" class="stat-value-skeleton"></div>
            <div v-else class="stat-value">{{ formatNumber(appStats.totalWallpapers) }}</div>
            <div class="stat-label">Wallpapers</div>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <div class="stat-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <div v-if="statsLoading" class="stat-value-skeleton"></div>
            <div v-else class="stat-value">{{ formatNumber(appStats.totalCategories) }}</div>
            <div class="stat-label">Categories</div>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <div class="stat-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div v-if="statsLoading" class="stat-value-skeleton"></div>
            <div v-else class="stat-value">{{ formatNumber(appStats.totalDownloads) }}</div>
            <div class="stat-label">Downloads</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Ad Rectangle after stats -->
    <SmartAd ad-type="rectangle" class="ad-spacer ad-center" :min-content-height="1000" />

  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { wallpaperService, type Wallpaper } from '../firebase/services/wallpaperService';
import { userService } from '../firebase/services/userService';
import { useAuth } from '../composables/useAuth';
import { useCategoryStore } from '../stores/categoryStore';
import WallpaperGrid from '../components/WallpaperGrid.vue';
import HeroSection from '../components/HeroSection.vue';
import SmartAd from '../components/SmartAd.vue';

// State
const featuredWallpapers = ref<Wallpaper[]>([]);
const loading = ref(false);
const userFavorites = ref<string[]>([]);
const viewMode = ref<'grid' | 'preview'>('grid');
const categoryMap = ref<Record<string, string>>({});

const { currentUser } = useAuth();

// Initialize stores
const categoryStore = useCategoryStore();

// Dynamic data state
const popularCategories = ref<Array<{ id: string; name: string; description: string; icon: string; count: number }>>([]);
const categoriesLoading = ref(true);
const appStats = ref({
  totalWallpapers: 0,
  totalCategories: 0,
  totalDownloads: 0
});
const statsLoading = ref(true);

// Category icons mapping
const categoryIcons: Record<string, string> = {
  'Nature': '🌿',
  'Abstract': '🎨',
  'Space': '🌌',
  'Architecture': '🏢',
  'Photography': '📸',
  'Technology': '💻',
  'Animals': '🐾',
  'Minimalist': '⚪',
  'Artistic': '🎭',
  'Gaming': '🎮',
  'Cars': '🏎️',
  'Anime': '⛩️',
  'Fantasy': '🧙',
  'Sci-Fi': '🚀',
  'Landscape': '🏞️',
  'Dark': '🌑',
  'Flowers': '🌸',
  'City': '🌃',
  'Ocean': '🌊',
  'Mountains': '⛰️',
  '3D Renders': '🧊',
  'Asphalt Legends': '🏁',
  'Uncategorized': '📁'
};

// Methods
const loadFeaturedWallpapers = async () => {
  loading.value = true;
  try {
    const result = await wallpaperService.getWallpapers({ limitCount: 24 });
    featuredWallpapers.value = result.wallpapers;
  } catch (error) {
    console.error('Error loading featured wallpapers:', error);
  } finally {
    loading.value = false;
  }
};

const loadUserFavorites = async () => {
  if (!currentUser.value) {
    userFavorites.value = [];
    return;
  }

  try {
    const favorites = await userService.getUserFavorites(currentUser.value.uid);
    userFavorites.value = favorites.map(fav => fav.wallpaperId);
  } catch (error) {
    console.error('Error loading user favorites:', error);
  }
};

const router = useRouter();

const viewWallpaper = (wallpaper: Wallpaper) => {
  router.push(`/wallpaper/${wallpaper.id}`);
};

// Dynamic data loading functions
const loadCategoryData = async () => {
  categoriesLoading.value = true;
  try {
    // Fetch all categories from the store
    await categoryStore.fetchCategories();

    // Get all categories with their counts
    const categories = categoryStore.categories;

    // Build categoryMap for WallpaperCard component
    categoryMap.value = categories.reduce((map, category) => {
      map[category.id] = category.name;
      return map;
    }, {} as Record<string, string>);

    // Transform categories for display
    const categoryData = categories.map(category => ({
      id: category.id,
      name: category.name,
      description: category.description,
      icon: categoryIcons[category.name] || '📷',
      count: category.wallpaperCount || 0
    }));

    // Sort by count and take top 5
    popularCategories.value = categoryData
      .filter(cat => cat.name !== 'Uncategorized')
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

  } catch (error) {
    console.error('Error loading category data:', error);
    // Fallback to default categories if error
    popularCategories.value = [
      { id: 'nature', name: 'Nature', description: 'Beautiful nature wallpapers', icon: '🌿', count: 0 },
      { id: 'abstract', name: 'Abstract', description: 'Abstract art and designs', icon: '🎨', count: 0 },
      { id: 'space', name: 'Space', description: 'Galaxies, planets, and stars', icon: '🌌', count: 0 },
      { id: 'architecture', name: 'Architecture', description: 'Stunning architectural designs', icon: '🏢', count: 0 },
      { id: 'photography', name: 'Photography', description: 'Professional photography', icon: '📸', count: 0 }
    ];
    
    // Build fallback categoryMap
    categoryMap.value = {
      'nature': 'Nature',
      'abstract': 'Abstract', 
      'space': 'Space',
      'architecture': 'Architecture',
      'photography': 'Photography'
    };
  } finally {
    categoriesLoading.value = false;
  }
};

const loadAppStats = async () => {
  statsLoading.value = true;
  try {
    // Get total wallpapers count
    const wallpapersResult = await wallpaperService.getWallpapers({ limitCount: 1000 });
    const totalWallpapers = wallpapersResult.wallpapers.length;

    // Get total categories count from the store
    await categoryStore.fetchCategories();
    const totalCategories = categoryStore.categories.length;

    // Calculate total downloads from all wallpapers
    const totalDownloads = wallpapersResult.wallpapers.reduce((sum, wallpaper) => {
      return sum + (wallpaper.downloadCount || 0);
    }, 0);

    appStats.value = {
      totalWallpapers,
      totalCategories,
      totalDownloads
    };

  } catch (error) {
    console.error('Error loading app stats:', error);
    // Keep default values on error
    appStats.value = {
      totalWallpapers: 0,
      totalCategories: 0,
      totalDownloads: 0
    };
  } finally {
    statsLoading.value = false;
  }
};

// Helper function to format numbers
const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M+`;
  } else if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K+`;
  } else if (num > 0) {
    return `${num}+`;
  }
  return '0';
};

// Lifecycle
onMounted(async () => {
  // Load featured wallpapers
  await loadFeaturedWallpapers();

  // Load dynamic data
  await Promise.all([
    loadCategoryData(),
    loadAppStats()
  ]);
});

watch(currentUser, async () => {
  await loadUserFavorites();
}, { immediate: true });
</script>

<style scoped>
/* ============================================
   HOME PAGE - Open, Airy, Premium Design
   ============================================ */

.home-page {
  min-height: 100vh;
}

/* ---- Section Container ---- */
.section-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 clamp(1rem, 4vw, 3rem);
}

/* ---- Section Headers (shared) ---- */
.section-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 2rem;
}

.section-header-left {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.section-label {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #9333ea;
}

.section-title {
  font-size: clamp(1.5rem, 3vw, 2rem);
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
}

:root.dark .section-title,
.dark .section-title {
  color: #f3f4f6;
}

.view-all-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #9333ea;
  text-decoration: none;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  transition: all 0.25s ease;
}

.view-all-link:hover {
  background: rgba(147, 51, 234, 0.08);
  gap: 0.75rem;
}

.view-all-arrow {
  width: 1rem;
  height: 1rem;
  transition: transform 0.25s ease;
}

.view-all-link:hover .view-all-arrow {
  transform: translateX(3px);
}

/* ---- Featured Wallpapers Section ---- */
.featured-section {
  padding: clamp(3rem, 6vw, 5rem) 0;
}

/* ---- View Mode Toggle ---- */
.view-toggle-wrap {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1.5rem;
}

.view-toggle {
  display: inline-flex;
  gap: 0.25rem;
  padding: 0.25rem;
  border-radius: 0.75rem;
  background: #f3f4f6;
}

.dark .view-toggle {
  background: #1f2937;
}

.view-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  font-size: 0.8rem;
  font-weight: 500;
  border-radius: 0.6rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #6b7280;
  background: transparent;
}

.dark .view-toggle-btn {
  color: #9ca3af;
}

.view-toggle-btn:hover {
  color: #374151;
}

.dark .view-toggle-btn:hover {
  color: #e5e7eb;
}

.view-toggle-btn.active {
  background: #fff;
  color: #111827;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.dark .view-toggle-btn.active {
  background: #374151;
  color: #f3f4f6;
}

/* ---- Categories Section ---- */
.categories-section {
  padding: clamp(3rem, 6vw, 5rem) 0;
  background: linear-gradient(180deg, transparent 0%, rgba(147, 51, 234, 0.03) 50%, transparent 100%);
}

.dark .categories-section {
  background: linear-gradient(180deg, transparent 0%, rgba(147, 51, 234, 0.06) 50%, transparent 100%);
}

.categories-scroll {
  display: flex;
  gap: 1.25rem;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 1rem;
  /* Hide scrollbar */
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.categories-scroll::-webkit-scrollbar {
  display: none;
}

.category-card {
  flex: 0 0 auto;
  width: clamp(180px, 20vw, 240px);
  scroll-snap-align: start;
  padding: 2rem 1.5rem;
  border-radius: 1.25rem;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  text-decoration: none;
  color: inherit;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.dark .category-card {
  background: #1f2937;
  border-color: rgba(255, 255, 255, 0.06);
}

.category-card::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(135deg, rgba(147, 51, 234, 0.08), rgba(236, 72, 153, 0.08));
  opacity: 0;
  transition: opacity 0.35s ease;
}

.category-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 20px 40px rgba(147, 51, 234, 0.12);
  border-color: rgba(147, 51, 234, 0.2);
}

.category-card:hover::before {
  opacity: 1;
}

.category-icon {
  font-size: 2.5rem;
  margin-bottom: 0.75rem;
  position: relative;
  z-index: 1;
  transition: transform 0.35s ease;
}

.category-card:hover .category-icon {
  transform: scale(1.15);
}

.category-name {
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.25rem;
  position: relative;
  z-index: 1;
}

.dark .category-name {
  color: #f3f4f6;
}

.category-desc {
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 0.5rem;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  position: relative;
  z-index: 1;
}

.dark .category-desc {
  color: #9ca3af;
}

.category-count {
  font-size: 0.75rem;
  font-weight: 600;
  color: #9333ea;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  background: rgba(147, 51, 234, 0.08);
  position: relative;
  z-index: 1;
}

/* Skeleton states */
.category-card.skeleton {
  pointer-events: none;
}

.skeleton-icon {
  width: 3rem;
  height: 3rem;
  border-radius: 0.75rem;
  background: #e5e7eb;
  animation: pulse 1.5s ease-in-out infinite;
  margin-bottom: 0.75rem;
}

.dark .skeleton-icon {
  background: #374151;
}

.skeleton-text-lg {
  width: 5rem;
  height: 1rem;
  border-radius: 0.375rem;
  background: #e5e7eb;
  animation: pulse 1.5s ease-in-out infinite;
  margin-bottom: 0.5rem;
}

.dark .skeleton-text-lg {
  background: #374151;
}

.skeleton-text-sm {
  width: 3.5rem;
  height: 0.75rem;
  border-radius: 0.375rem;
  background: #e5e7eb;
  animation: pulse 1.5s ease-in-out infinite;
}

.dark .skeleton-text-sm {
  background: #374151;
}

/* ---- Stats Section ---- */
.stats-section {
  padding: clamp(3rem, 6vw, 5rem) 0;
}

.stats-grid {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(1.5rem, 4vw, 4rem);
  padding: clamp(2rem, 4vw, 3.5rem) clamp(1.5rem, 4vw, 4rem);
  border-radius: 1.5rem;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.dark .stats-grid {
  background: #1f2937;
  border-color: rgba(255, 255, 255, 0.06);
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.5rem;
}

.stat-icon {
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
  background: rgba(147, 51, 234, 0.08);
  color: #9333ea;
}

.stat-icon svg {
  width: 1.25rem;
  height: 1.25rem;
}

.stat-value {
  font-size: clamp(1.75rem, 3vw, 2.5rem);
  font-weight: 800;
  background: linear-gradient(135deg, #9333ea, #ec4899);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1.1;
}

.stat-value-skeleton {
  width: 4rem;
  height: 2rem;
  border-radius: 0.5rem;
  background: #e5e7eb;
  animation: pulse 1.5s ease-in-out infinite;
}

.dark .stat-value-skeleton {
  background: #374151;
}

.stat-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.dark .stat-label {
  color: #9ca3af;
}

.stat-divider {
  width: 1px;
  height: 4rem;
  background: linear-gradient(180deg, transparent, rgba(147, 51, 234, 0.2), transparent);
}

/* ---- Ad Spacing ---- */
.ad-spacer {
  padding: 2rem clamp(1rem, 4vw, 3rem);
  max-width: 1400px;
  margin: 0 auto;
}

.ad-center {
  display: flex;
  justify-content: center;
}

/* ---- Animations ---- */
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

/* ---- Responsive ---- */
@media (max-width: 640px) {
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .stats-grid {
    flex-direction: column;
    gap: 1.5rem;
  }

  .stat-divider {
    width: 4rem;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(147, 51, 234, 0.2), transparent);
  }
}
</style>